import * as React from "react";
import { useNavigate, useParams } from "react-router";
import { dataProvider } from "@/providers/dataProvider";
import type { AdminRole } from "@/providers/types";
import type { Operator } from "@/pages/operators/list";
import { RoleBadge } from "@/components/RoleBadge";
import { RoleSelector } from "@/components/RoleSelector";
import { InlineError } from "@/components/InlineError";
import { SkeletonTable } from "@/components/SkeletonTable";

function mapErrorCode(code: string | undefined): string | null {
  if (code === "admin.last_superadmin_protected") {
    return "Нельзя понизить последнего superadmin";
  }
  if (code === "admin.self_role_change") {
    return "Нельзя изменить собственную роль";
  }
  return null;
}

export function OperatorsEdit(): React.JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [operator, setOperator] = React.useState<Operator | null>(null);
  const [selectedRole, setSelectedRole] = React.useState<AdminRole>("support");
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!id) return;

    let cancelled = false;

    setIsLoading(true);
    setLoadError(null);

    void dataProvider
      .getOne({ resource: "operators", id })
      .then((result) => {
        if (cancelled) return;
        const op = result.data as unknown as Operator;
        setOperator(op);
        setSelectedRole(op.role);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const bffErr = err as { message?: string } | null;
        setLoadError(bffErr?.message ?? "Ошибка загрузки оператора");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleSave(): Promise<void> {
    if (!id || !operator) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await dataProvider.update({
        resource: "operators",
        id,
        variables: { role: selectedRole },
      });
      navigate("/operators");
    } catch (err: unknown) {
      const bffErr = err as { code?: string; message?: string } | null;
      const mapped = mapErrorCode(bffErr?.code);
      setSaveError(mapped ?? bffErr?.message ?? "Ошибка сохранения");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <SkeletonTable rows={3} columns={2} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <p className="text-[var(--danger)] text-[14px]">{loadError}</p>
        <button
          type="button"
          onClick={() => navigate("/operators")}
          className="mt-4 text-[var(--accent)] text-[13px] font-[500] hover:underline min-h-[44px]"
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  if (!operator) return <></>;

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-6 py-5 border-b border-[var(--border)]">
        <h1 className="text-[22px] font-[700] tracking-[-0.3px]">Редактирование роли</h1>
        <p className="text-[13px] text-[var(--muted)] mt-1">{operator.email}</p>
      </div>

      <div className="p-6 flex flex-col gap-5 max-w-[440px]">
        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]">
            Email
          </span>
          <span className="text-[14px] font-[500] text-[var(--fg)]">{operator.email}</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]">
            Текущая роль
          </span>
          <span>
            <RoleBadge role={operator.role} />
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="edit-role-selector"
            className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]"
          >
            Новая роль
          </label>
          <RoleSelector
            id="edit-role-selector"
            value={selectedRole}
            onChange={setSelectedRole}
            disabled={isSaving}
          />
        </div>

        {saveError && <InlineError message={saveError} />}

        <div className="flex gap-2 pt-2 border-t border-[var(--border)]">
          <button
            type="button"
            onClick={() => navigate("/operators")}
            disabled={isSaving}
            className="px-5 py-2 bg-[var(--surface)] text-[var(--fg)] text-[13px] font-[600] border border-[var(--border)] rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)] disabled:opacity-50 transition-colors min-h-[44px]"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => { void handleSave(); }}
            disabled={isSaving || selectedRole === operator.role}
            className="px-5 py-2 bg-[var(--accent)] text-white text-[13px] font-[600] rounded-[var(--radius-sm)] hover:bg-[#155bd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
          >
            {isSaving ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}
